/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  RefreshControl,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { VIRTUALIZED_LIST_PROPS } from '../../shared/ui/virtualizedListProps';
import { useConsultationsList } from '../hooks/useConsultationsList';
import type { ConsultationFilter, ConsultationRow } from '../infra/consultationsRepo';
import { ticketColors } from '../../shared/theme/ticketColors';
import { status as statusTokens } from '@vuelto/tokens';
import { formatRelativeDayTime } from '@shared/vueltoDateTime';

const FILTER_TABS: { key: ConsultationFilter; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'unread', label: 'Sin leer' },
  { key: 'pending', label: 'Pendientes' },
  { key: 'answered', label: 'Respondidas' },
  { key: 'closed', label: 'Cerradas' },
];

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  answered: 'Respondida',
  closed: 'Cerrada',
};

const STATUS_COLORS: Record<string, string> = {
  pending: statusTokens.consultationPending,
  answered: statusTokens.consultationAnswered,
  closed: statusTokens.consultationClosed,
};

type ConsultationsScreenProps = {
  /** When embedded in OrdersInbox, pass `OrdersInbox` so the web header refresh button works. */
  refreshRouteName?: string;
  /** Prefer unread when the inbox knows there are unread consultations. */
  initialFilter?: ConsultationFilter;
};

export function ConsultationsScreen({
  refreshRouteName = 'Consultations',
  initialFilter = 'all',
}: ConsultationsScreenProps) {
  const navigation = useNavigation<NativeStackNavigationProp<{ ConsultationDetail: { consultationId: string } }>>();
  const { entitlements } = usePlanEntitlements();
  const orgId = entitlements?.organization_id;
  const canView = useModulePermission('BranchSettings', 'view');
  const canViewSensitive = useModulePermission('BranchSettings', 'view_sensitive');
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const [filter, setFilter] = useState<ConsultationFilter>(initialFilter);
  const { consultations, loading, hasMore, loadMore, reload } = useConsultationsList(
    canView ? orgId : undefined,
    filter,
    currentBranchId,
  );

  const load = useCallback(async () => {
    reload();
  }, [reload]);

  useScreenLoad(refreshRouteName, load);

  // Carga al montar + al cambiar filtro. No depender solo de useFocusEffect:
  // embebido en OrdersInbox el focus del padre ya está activo y el segment
  // mount no re-dispara focus → lista vacía hasta tocar un chip.
  useEffect(() => {
    reload();
  }, [filter, reload]);

  if (!canView) {
    return (
      <ShellContentFrame style={styles.container}>
        <ModuleEmptyState
          title="No podés ver Consultas"
          subtitle="Pedile al dueño que te dé permiso."
          testID="consultations.no-permission"
        />
      </ShellContentFrame>
    );
  }

  const renderItem = ({ item }: { item: ConsultationRow }) => {
    const isUnread = !item.read_by_store_at && item.status === 'pending';
    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigation.navigate('ConsultationDetail', { consultationId: item.id })}
        accessibilityRole="button"
        accessibilityLabel={`Consulta de ${item.customer_name}: ${item.subject}`}
        accessibilityHint={`Estado: ${STATUS_LABELS[item.status] ?? item.status}${isUnread ? ', no leída' : ''}`}
        testID="consultations.card"
      >
        <View style={styles.cardHeader}>
          <Text style={[styles.cardTitle, isUnread && styles.cardTitleUnread]}>{item.subject}</Text>
          <View style={[styles.statusBadge, { backgroundColor: STATUS_COLORS[item.status] ?? statusTokens.consultationClosed }]} testID="consultations.card.status">
            <Text style={styles.statusText}>{STATUS_LABELS[item.status] ?? item.status}</Text>
          </View>
        </View>
        <Text style={styles.customer}>{item.customer_name}</Text>
        {canViewSensitive && item.customer_dni && <Text style={styles.dni}>DNI: {item.customer_dni}</Text>}
        <Text style={styles.date}>{formatRelativeDayTime(item.created_at)}</Text>
        {item.branch_name && <Text style={styles.branch}>{item.branch_name}</Text>}
        {isUnread && <View style={styles.unreadDot} />}
      </TouchableOpacity>
    );
  };

  return (
    <ShellContentFrame style={styles.container}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.filterRow}
        contentContainerStyle={styles.filterRowContent}
        accessibilityRole="tablist"
      >
        {FILTER_TABS.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.filterTab, filter === tab.key && styles.filterTabActive]}
            onPress={() => setFilter(tab.key)}
            accessibilityRole="tab"
            accessibilityLabel={`Filtro ${tab.label}`}
            accessibilityState={{ selected: filter === tab.key }}
            testID={`consultations.filter.${tab.key}`}
          >
            <Text style={[styles.filterTabText, filter === tab.key && styles.filterTabTextActive]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <FlatList
        data={consultations}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={reload} />}
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        contentContainerStyle={consultations.length === 0 ? styles.emptyContainer : undefined}
        ListEmptyComponent={
          !loading ? (
            <ModuleEmptyState
              title="No hay consultas"
              subtitle={
                filter === 'all'
                  ? 'Cuando un cliente pregunte algo, aparece acá.'
                  : 'Probá con otro filtro.'
              }
              testID="consultations.empty"
            />
          ) : null
        }
        ListFooterComponent={
          hasMore ? <Text style={styles.loadMore}>Cargando más…</Text> : null
        }
        {...VIRTUALIZED_LIST_PROPS}
      />
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: ticketColors.paper },
  filterRow: { flexGrow: 0 },
  filterRowContent: { flexDirection: 'row', paddingHorizontal: 12, paddingVertical: 8, gap: 6 },
  filterTab: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, backgroundColor: ticketColors.paper2 },
  filterTabActive: { backgroundColor: ticketColors.gold },
  filterTabText: { fontSize: 12, fontWeight: '600', color: ticketColors.ink },
  filterTabTextActive: { color: ticketColors.goldInk },
  card: {
    backgroundColor: ticketColors.card,
    borderRadius: 14,
    padding: 16,
    marginHorizontal: 12,
    marginVertical: 4,
    borderWidth: 1,
    borderColor: ticketColors.border,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontSize: 16, fontWeight: '600', color: ticketColors.ink, flex: 1, marginRight: 8 },
  cardTitleUnread: { fontWeight: '700' },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  statusText: { fontSize: 11, fontWeight: '700', color: '#fff' },
  customer: { fontSize: 14, fontWeight: '600', color: ticketColors.ink, marginTop: 8 },
  dni: { fontSize: 13, color: ticketColors.inkLight, marginTop: 2 },
  date: { fontSize: 11, color: ticketColors.inkLight, marginTop: 2 },
  branch: { fontSize: 11, color: ticketColors.inkLight, marginTop: 2 },
  unreadDot: { position: 'absolute', top: 12, right: 12, width: 8, height: 8, borderRadius: 4, backgroundColor: ticketColors.gold },
  emptyContainer: { flex: 1, justifyContent: 'center' },
  loadMore: { textAlign: 'center', padding: 12, fontSize: 13, color: ticketColors.inkLight },
});
